import type { TicketRes } from '../res'
import { ticketType } from '../type'

export type Ticket = Record<string, unknown>

export type TicketLike = TicketRes | Ticket

/**
 * カテゴリごとの中カテゴリマッピング
 */
export const SUBCATEGORIES_BY_CATEGORY: Record<
  ticketType.category,
  (typeof ticketType.TICKET_SUBCATEGORIES)[number][]
> = {
  toiawase: ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    [
      'kagi-autorokku',
      'merubokkusu-takuhai-bokkusu',
      'intanetto-terebi-denwa',
      'eakon-kanren',
      'setsubi-kanren-shiyo-hoho-to',
      'chushajo-churinjo',
      'yachin-suido-dai',
      'hosho-gaisha',
      'shorui-kankei',
      'hoken',
      'koshin-saikeiyaku',
      'meigi-henko',
      'nyukyosha-joho-henko',
      'raifurain',
      'kyoju-ruru',
      'anpi-kyoju-kakunin',
      'sonota-toiawase',
    ].includes(item.key),
  ),
  'kaiyaku-kanren': ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    [
      'kaiyaku-tetsuzuki-hoho',
      'kaiyaku-bi-henko-torikeshi',
      'taikyo-tachiai',
      'taikyo-seisan',
      'shikikin-henkin-koza-tenkyo-saki-jusho',
    ].includes(item.key),
  ),
  'kuremu-hado': ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    [
      'eakon',
      'kyutoki',
      'nyukyoji-fuguai',
      'mizumawari',
      'toire-woshuretto',
      'rosui',
      'intahon-chaimu',
      'konro',
      'tategu',
      'terebi',
      'intanetto',
      'kyoyobu',
      'kagi-genkan',
      'kanki-kanren',
      'shomei-kigu',
      'bureka-denki-kanren',
      'keihoki',
      'kurosu-yuka-tatami',
      'kabi-ketsuro',
    ].includes(item.key),
  ),
  'kuremu-sofuto': ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    [
      'chushajo',
      'churinjo',
      'so-on',
      'gomi',
      'shokubutsu',
      'seibutsu',
      'kitsuen',
      'nioi',
      'kyoyobu-no-shiyo-ni-tsuite-shibutsu',
      'kyoyobu-no-yogore-seiso',
    ].includes(item.key),
  ),
  sonota: ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    ['sonota'].includes(item.key),
  ),
  'sabisu-ni-tsuite': ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    ['sabisu-toiawase'].includes(item.key),
  ),
  'shinki-nyukyosha-shonin': ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    ['shinki-keiyaku-shonin-kakunin'].includes(item.key),
  ),
  'kaiyaku-uketsuke': ticketType.TICKET_SUBCATEGORIES.filter((item) =>
    ['kaiyaku-uketsuke-kakunin'].includes(item.key),
  ),
}

/**
 * カテゴリに対応する中カテゴリ一覧を取得
 * @param category - カテゴリキー（例: 'toiawase'）
 * @returns 対応する中カテゴリの配列。該当なしの場合は空配列
 */
export function getSubcategoriesByCategory(
  category: ticketType.category,
): (typeof ticketType.TICKET_SUBCATEGORIES)[number][] {
  return SUBCATEGORIES_BY_CATEGORY[category] || []
}

/**
 * チケットカテゴリのキーから表示ラベルを取得
 * @param category - カテゴリキー（例: 'toiawase'）
 * @returns 表示ラベル（例: '問い合せ'）。該当なしの場合はキーをそのまま返す
 */
export function getCategoryLabel(category: string): string {
  const categoryItem = ticketType.TICKET_CATEGORIES.find(
    (item) => item.key === category,
  )
  return categoryItem?.value || category
}

/**
 * チケットサブカテゴリのキーから表示ラベルを取得
 * @param subCategory - サブカテゴリキー（例: 'eakon'）
 * @returns 表示ラベル（例: 'エアコン'）。該当なしの場合はキーをそのまま返す
 */
export function getSubCategoryLabel(subCategory: string): string {
  const subCategoryItem = ticketType.TICKET_SUBCATEGORIES.find(
    (item) => item.key === subCategory,
  )
  return subCategoryItem?.value || subCategory
}

/**
 * 業者対応状況のキーから表示ラベルを取得
 * @param contractorStatus - 業者対応状況キー（例: 'estimate_requested'）
 * @returns 表示ラベル（例: '見積依頼中'）。null/undefinedの場合は'-'を返す
 */
export function getContractorStatusLabel(
  contractorStatus: string | null | undefined,
): string {
  if (!contractorStatus) return '-'
  const statusItem = ticketType.CONTRACTOR_STATUSES.find(
    (item) => item.key === contractorStatus,
  )
  return statusItem?.value || contractorStatus
}

/**
 * 業者種別のキーから表示ラベルを取得
 * @param contractorType - 業者種別キー（例: 'agentcall'）
 * @returns 表示ラベル（例: 'エージェントコール業者'）。null/undefinedの場合は'-'を返す
 */
export function getContractorTypeLabel(
  contractorType: string | null | undefined,
): string {
  if (!contractorType) return '-'
  const typeItem = ticketType.CONTRACTOR_TYPES.find(
    (item) => item.key === contractorType,
  )
  return typeItem?.value || contractorType
}

/**
 * 連絡種別のキーから表示ラベルを取得
 * @param contactType - 連絡種別キー（例: 'phone'）
 * @returns 表示ラベル（例: '電話'）。該当なしの場合はキーをそのまま返す
 */
export function getContactTypeLabel(contactType: string): string {
  const typeItem = ticketType.CONTACT_TYPES.find(
    (item) => item.key === contactType,
  )
  return typeItem?.value || contactType
}

/**
 * 連絡者カテゴリのキーから表示ラベルを取得
 * @param category - 連絡者カテゴリキー（例: 'oc'）
 * @returns 表示ラベル（例: 'OC'）。該当なしの場合はキーをそのまま返す
 */
export function getContactCategoryLabel(
  category: ticketType.contactFromCategory | ticketType.contactToCategory,
): string {
  const categoryItem = ticketType.CONTACT_CATEGORIES.find(
    (item) => item.key === category,
  )
  return categoryItem?.value || category
}

/**
 * 連絡者情報から表示名を取得
 * @param category - 連絡者カテゴリ
 * @param contacts - 連絡者情報オブジェクト
 * @returns 表示名（例: 'OC：山田太郎'）
 */
export function getContactDisplayName(
  category: ticketType.contactFromCategory | ticketType.contactToCategory,
  contacts: {
    owner?: { name?: string | null } | null
    tenant?: { name?: string | null } | null
    admin?: { name?: string | null } | null
    text?: string | null
  },
): string {
  const categoryLabel = getContactCategoryLabel(category)

  switch (category) {
    case ticketType.CONTACT_CATEGORY_OWNER:
      return contacts.owner?.name
        ? `${categoryLabel}：${contacts.owner.name}`
        : categoryLabel
    case ticketType.CONTACT_CATEGORY_TENANT:
    case ticketType.CONTACT_CATEGORY_RESIDENT:
      return contacts.tenant?.name
        ? `${categoryLabel}：${contacts.tenant.name}`
        : categoryLabel
    case ticketType.CONTACT_CATEGORY_OC:
      return contacts.admin?.name
        ? `${categoryLabel}：${contacts.admin.name}`
        : categoryLabel
    case ticketType.CONTACT_CATEGORY_CONTRACTOR:
    case ticketType.CONTACT_CATEGORY_OTHER:
      return contacts.text
        ? `${categoryLabel}：${contacts.text}`
        : categoryLabel
    case ticketType.CONTACT_CATEGORY_EMERGENCY:
      return categoryLabel
    default:
      return categoryLabel
  }
}

/**
 * チケットオブジェクトから連絡者名を取得するヘルパー
 * @param ticket - チケットオブジェクト
 * @param direction - 'from'（連絡元）または 'to'（連絡先）
 * @returns 連絡者名
 */
export function getTicketContactName<
  T extends {
    contactFromCategory?: string | null
    contactToCategory?: string | null
    contactFromOwner?: { name?: string | null } | null
    contactToOwner?: { name?: string | null } | null
    contactFromTenant?: { name?: string | null } | null
    contactToTenant?: { name?: string | null } | null
    contactFromAdmin?: { name?: string | null } | null
    contactToAdmin?: { name?: string | null } | null
    contactFromText?: string | null
    contactToText?: string | null
  },
>(ticket: T, direction: 'from' | 'to'): string {
  const prefix = direction === 'from' ? 'contactFrom' : 'contactTo'
  const categoryKey = `${prefix}Category` as keyof T
  const category = ticket[categoryKey] as string | null | undefined

  if (!category) return '不明'

  const contacts = {
    owner:
      direction === 'from' ? ticket.contactFromOwner : ticket.contactToOwner,
    tenant:
      direction === 'from' ? ticket.contactFromTenant : ticket.contactToTenant,
    admin:
      direction === 'from' ? ticket.contactFromAdmin : ticket.contactToAdmin,
    text: direction === 'from' ? ticket.contactFromText : ticket.contactToText,
  }

  return getContactDisplayName(
    category as ticketType.contactFromCategory,
    contacts,
  )
}
